/**
 * Fan sign-in (`/fans/login`).
 *
 * Deliberately NOT the staff `/login`. That page opens by demanding a team
 * code, which a fan does not have and has no way to obtain — it is the single
 * fastest way to lose the audience this page exists for.
 *
 * A fan account lives in the shared `public-fanatics` realm, so the tenant is
 * resolved server-side via `GET /auth/v1/fan/sign-in-options` and never asked
 * for. MFA is opt-in per account and off by default, so there is no TOTP step
 * here at all; a fan who did turn it on is routed to the shared MFA screen by
 * the session context.
 *
 * `?next=` is preserved so a fan who followed a team deep link lands back on
 * that team after signing in (F7) rather than on a generic home page.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { TextField } from '../../components/text-field';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { ApiError } from '../../lib/api-error';
import { ROUTES } from '../../lib/constants';

import {
  FanAuthChrome,
  FanAuthSwitch,
  FanSocialButtons,
  StaffAuthHint,
  useFanSignInOptions,
} from './fan-auth-shared';

function toSignInMessage(error: unknown): string {
  if (ApiError.isApiError(error)) {
    // 401 is the deliberate anti-enumeration answer for both "no such account"
    // and "wrong password"; say so in one sentence rather than guessing which.
    if (error.status === 401) return 'That email and password do not match. Try again.';
    if (error.status === 403) return 'This account has been disabled. Contact support for help.';
    if (error.status === 429) return 'Too many attempts. Wait a moment and try again.';
    return error.message;
  }
  return 'Could not sign in. Check your connection and try again.';
}

export function FanLoginPage(): JSX.Element {
  useDocumentTitle('Fan sign in');
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startSession, isAuthenticated } = useSession();
  const { options, isLoading } = useFanSignInOptions();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const nextPath = searchParams.get('next');
  const destination = nextPath ?? ROUTES.fanaticPortal;

  useEffect(() => {
    if (isAuthenticated) navigate(destination, { replace: true });
  }, [isAuthenticated, navigate, destination]);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const errors: Record<string, string> = {};
    if (email.trim() === '') errors.email = 'Enter your email address.';
    if (password === '') errors.password = 'Enter your password.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setFormError(null);
    setIsBusy(true);
    try {
      const tokens = await apiClient.login({
        // The fan realm, resolved by the server — never typed by the fan.
        tenantId: options?.realm_tenant_id ?? '',
        email: email.trim(),
        password,
      });
      startSession(tokens, null);
      setPassword('');
      navigate(destination, { replace: true });
    } catch (error) {
      setFormError(toSignInMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <FanAuthChrome
      title="Welcome back"
      subtitle="Sign in to follow your teams and watch their games."
    >
      {formError ? (
        <div className="banner banner--error" role="alert">
          <span className="banner__title">Sign-in failed</span>
          <span>{formError}</span>
        </div>
      ) : null}

      <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
        <TextField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          error={fieldErrors.email ?? null}
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextField
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          error={fieldErrors.password ?? null}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={isBusy || isLoading}
        >
          {isBusy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <FanSocialButtons
        options={options}
        redirectUri={`${window.location.origin}${ROUTES.oauthCallback}`}
        isBusy={isBusy}
      />

      <p className="auth__alt">
        <a href={ROUTES.forgotPassword}>Forgot your password?</a>
      </p>
      <FanAuthSwitch mode="login" />
      <StaffAuthHint />
    </FanAuthChrome>
  );
}
